import React, { useState, useEffect } from 'react'
import { Link, useLocation, useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Typography, Button, Box, Tabs, Tab } from '@material-ui/core'
import { ArrowBack as ArrowLeft } from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { Routes } from '../../../../navigation/config/routes'
import { TabPanel } from '../../../../components/TabPanel/TabPanel'
import { CampaignSetupTab } from './CampaignSetupTab'
import { CampaignSequenceTab } from './CampaignSequenceTab'
import { CampaignLaunchTab } from './CampaignLaunchTab'
import { SequenceItem, SequenceStep, CampaignData, CTAOption } from './types'
import { useCampaign } from '../../../../hooks/Outreach/useCampaign'
import { axiosInstance } from '../../../../helpers'
import { Campaign } from '../../../../models/Outreach/types'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        width: '100%',
        padding: theme.spacing(3),
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(3),
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    title: {
        fontSize: '2rem',
        fontWeight: 'bold',
        color: '#1f2937',
        marginBottom: theme.spacing(0.5),
    },
    subtitle: {
        color: '#6b7280',
        fontSize: '1rem',
    },
    backButton: {
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '0.875rem',
        fontWeight: 500,
        borderColor: '#d1d5db',
        color: '#6b7280',
        '&:hover': {
            borderColor: '#3b82f6',
            color: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.04)',
        },
    },
    tabsContainer: {
        backgroundColor: '#ffffff',
        border: '1px solid rgba(226, 232, 240, 0.5)',
        borderRadius: theme.spacing(1),
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: theme.spacing(3),
        padding: theme.spacing(0.5),
    },
    tab: {
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        minWidth: 120,
        borderRadius: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        padding: theme.spacing(1, 2),
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
        },
        '&.Mui-selected': {
            backgroundColor: '#3b82f6',
            color: 'white',
            fontWeight: 600,
        },
        '&.Mui-disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
}))

export const CreateCampaign = () => {
    const classes = useStyles()
    const location = useLocation()
    const history = useHistory()
    const aiGeneratedData = (location.state as any)?.aiGeneratedData
    const { createCampaign } = useCampaign()

    const [campaignData, setCampaignData] = useState<CampaignData>({
        name: '',
        channels: {
            email: false,
            sms: false,
        },
        cta: 'schedule-tour',
        reply_to_email: '',
        sender_name: '',
        concessions: [],
    })

    const [sequence, setSequence] = useState<SequenceItem[]>([])
    const [activeTab, setActiveTab] = useState(0)

    // Pre-fill with AI-generated data if available
    useEffect(() => {
        if (aiGeneratedData) {
            setCampaignData({
                name: aiGeneratedData.name || '',
                channels: aiGeneratedData.channels || {
                    email: false,
                    sms: false,
                },
                cta: aiGeneratedData.cta || 'schedule-tour',
                reply_to_email: aiGeneratedData.reply_to_email || '',
                sender_name: aiGeneratedData.sender_name || '',
            })

            // Convert AI sequence to SequenceItem format
            if (
                aiGeneratedData.sequence &&
                aiGeneratedData.sequence.length > 0
            ) {
                const convertedSequence: SequenceItem[] = aiGeneratedData.sequence
                    .filter(
                        (step: any) =>
                            step.type === 'email' || step.type === 'sms',
                    )
                    .map((step: any, index: number) => ({
                        id: `step-${index + 1}`,
                        type: step.type as 'email' | 'sms',
                        subject: step.subject || '',
                        message: step.message || '',
                        condition: step.condition,
                    }))
                setSequence(convertedSequence)
            }

            toast.info(
                'AI Campaign Loaded - Your AI-generated campaign has been loaded. Review and edit as needed.',
            )
        }
    }, [aiGeneratedData])

    const ctaOptions: CTAOption[] = [
        { value: 'schedule-tour', label: 'Schedule a Tour' },
        { value: 'request-info', label: 'Request More Info' },
        { value: 'apply-now', label: 'Apply Now' },
        { value: 'call-us', label: 'Call Us Today' },
    ]

    // Validation functions
    const isSetupComplete = () => {
        return (
            campaignData.name.trim() !== '' &&
            campaignData.reply_to_email.trim() !== '' &&
            campaignData.sender_name.trim() !== '' &&
            (campaignData.channels.email || campaignData.channels.sms)
        )
    }

    const isSequenceComplete = () => {
        const messageSteps = sequence.filter(
            (item): item is SequenceStep =>
                item.type === 'email' || item.type === 'sms',
        )
        return (
            messageSteps.length > 0 &&
            messageSteps.every((step) => !!step.guest_card)
        )
    }

    const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
        // Prevent navigation to tabs that aren't ready
        if (newValue === 1 && !isSetupComplete()) {
            return
        }
        if (newValue === 2 && (!isSetupComplete() || !isSequenceComplete())) {
            return
        }
        setActiveTab(newValue)
    }

    return (
        <Box className={classes.container}>
            {/* Header */}
            <Box className={classes.header}>
                <Box className={classes.headerContent}>
                    <Link
                        to={Routes.manage.campaign.root}
                        style={{ textDecoration: 'none' }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ArrowLeft />}
                            className={classes.backButton}
                        >
                            Back to Campaigns
                        </Button>
                    </Link>
                    <Box>
                        <Typography variant="h4" className={classes.title}>
                            Create New Campaign
                        </Typography>
                        <Typography
                            variant="body1"
                            className={classes.subtitle}
                        >
                            Design and launch your multi-step marketing campaign
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Campaign Setup Tabs */}
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                className={classes.tabsContainer}
                TabIndicatorProps={{ style: { display: 'none' } }}
                variant="fullWidth"
            >
                <Tab
                    label="Campaign Setup"
                    className={classes.tab}
                    disabled={false}
                />
                <Tab
                    label="Message Sequence"
                    className={classes.tab}
                    disabled={!isSetupComplete()}
                />
                <Tab
                    label="Review & Launch"
                    className={classes.tab}
                    disabled={!isSetupComplete() || !isSequenceComplete()}
                />
            </Tabs>

            {/* Setup Tab */}
            <TabPanel value={activeTab} index={0}>
                <CampaignSetupTab
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    ctaOptions={ctaOptions}
                    sequence={sequence}
                    onNext={() => setActiveTab(1)}
                />
            </TabPanel>

            {/* Sequence Tab */}
            <TabPanel value={activeTab} index={1}>
                <CampaignSequenceTab
                    sequence={sequence}
                    setSequence={setSequence}
                    channels={campaignData.channels}
                    campaignName={campaignData.name}
                    campaignGoal={campaignData.cta}
                    onBack={() => setActiveTab(0)}
                    onNext={() => setActiveTab(2)}
                />
            </TabPanel>

            {/* Launch Tab */}
            <TabPanel value={activeTab} index={2}>
                <CampaignLaunchTab
                    campaignData={campaignData}
                    sequence={sequence}
                    onBack={() => setActiveTab(1)}
                    onCreateCampaign={async () => {
                        // Validate campaign name
                        if (!campaignData.name.trim()) {
                            toast.error('Campaign name is required')
                            return
                        }

                        // Validate reply-to email
                        if (!campaignData.reply_to_email.trim()) {
                            toast.error('Reply-to email is required')
                            return
                        }

                        // Validate sender name
                        if (!campaignData.sender_name.trim()) {
                            toast.error('Sender name is required')
                            return
                        }

                        // Validate at least one step exists
                        if (sequence.length === 0) {
                            toast.error('At least one step is required')
                            return
                        }

                        // Filter message steps (email or sms)
                        const messageSteps = sequence.filter(
                            (item) =>
                                item.type === 'email' || item.type === 'sms',
                        )

                        // Validate at least one message step exists
                        if (messageSteps.length === 0) {
                            toast.error(
                                'At least one message step (email or SMS) is required',
                            )
                            return
                        }

                        // Validate each message step
                        for (let i = 0; i < messageSteps.length; i++) {
                            const step = messageSteps[i] as SequenceStep
                            const stepNumber = sequence.indexOf(step) + 1

                            // Validate message content
                            if (!step.message || !step.message.trim()) {
                                toast.error(
                                    `Step ${stepNumber}: Message content is required`,
                                )
                                return
                            }

                            // Validate subject for email steps
                            if (step.type === 'email') {
                                if (!step.subject || !step.subject.trim()) {
                                    toast.error(
                                        `Step ${stepNumber}: Email subject is required`,
                                    )
                                    return
                                }
                            }
                        }

                        // Collect pending files per step index before creating
                        const pendingFilesByStepIndex: Record<
                            number,
                            File[]
                        > = {}
                        let messageStepIdx = 0
                        sequence.forEach((item) => {
                            if (item.type === 'email' || item.type === 'sms') {
                                const step = item as SequenceStep
                                if (
                                    step.pendingFiles &&
                                    step.pendingFiles.length > 0
                                ) {
                                    pendingFilesByStepIndex[messageStepIdx] =
                                        step.pendingFiles
                                }
                                messageStepIdx++
                            }
                        })

                        createCampaign.mutate(
                            {
                                name: campaignData.name.trim(),
                                campaignGoal: campaignData.cta,
                                sequence: sequence,
                                replyToEmail: campaignData.reply_to_email.trim(),
                                senderName: campaignData.sender_name.trim(),
                                concessions: campaignData.concessions,
                            },
                            {
                                onSuccess: async (response) => {
                                    const campaign: Campaign = response.data
                                    const campaignId = campaign?.id

                                    // Upload pending files to their corresponding steps
                                    if (campaignId && campaign.steps) {
                                        const emailSteps = campaign.steps.filter(
                                            (s) =>
                                                s.step_type === 'EMAIL' ||
                                                s.step_type === 'TEXT_MESSAGE',
                                        )
                                        const uploadPromises: Promise<any>[] = []

                                        Object.entries(
                                            pendingFilesByStepIndex,
                                        ).forEach(([idxStr, files]) => {
                                            const idx = parseInt(idxStr, 10)
                                            const step = emailSteps[idx]
                                            if (!step) return
                                            files.forEach((file) => {
                                                const formData = new FormData()
                                                formData.append('file', file)
                                                uploadPromises.push(
                                                    axiosInstance.post(
                                                        `/outreach/campaigns/${campaignId}/steps/${step.id}/attachments/`,
                                                        formData,
                                                        {
                                                            headers: {
                                                                'Content-Type':
                                                                    'multipart/form-data',
                                                            },
                                                        },
                                                    ),
                                                )
                                            })
                                        })

                                        if (uploadPromises.length > 0) {
                                            try {
                                                await Promise.all(
                                                    uploadPromises,
                                                )
                                            } catch {
                                                toast.error(
                                                    'Some attachments failed to upload. You can add them by editing the campaign.',
                                                )
                                            }
                                        }
                                    }

                                    if (campaignId) {
                                        history.push(
                                            Routes.manage.campaign.detail.replace(
                                                ':id',
                                                campaignId.toString(),
                                            ),
                                        )
                                    } else {
                                        history.push(
                                            Routes.manage.campaign.root,
                                        )
                                    }
                                },
                            },
                        )
                    }}
                    isCreating={createCampaign.isLoading}
                />
            </TabPanel>
        </Box>
    )
}
